import * as React from "react";
import { v4 as uuid } from "uuid";
import { useParams, Navigate } from "react-router-dom";
import { Typography, Snackbar } from "@material-ui/core";

import { Page } from "./components/layout";
import { Loading } from "./components/misc";
import WrapUpStoryEditor from "./components/wrapUpStories/WrapUpStoryEditor";
import { useUserContext } from "./components/auth/UserContextProvider";
import { GET_BEACON_WRAP_UP_STORY, GET_BEACON_ITEMS } from "../api/graphQL/queries";
import { BeaconWrapUpStory, GraphQlConnection, BeaconItem } from "../api/graphQL/queryModels";
import { useQuery, useMutation, gql } from "@apollo/client";
import { BeaconWrapUpStoryInput } from "../api/graphQL/mutationModels";
import { UPSERT_BEACON_WRAP_UP_STORY } from "../api/graphQL/mutations";
import { useNotifications } from "./components/notifications/NotificationContextProvider";
import DocumentTitle from "./components/DocumentTitle";
import { BEACON_WRAP_UP_STORY_CORE_FIELDS_FRAGMENT } from "../api/graphQL/fragments";

const WrapUpStoryEditorPage = () => {
    const { id } = useParams<{ id: string }>();
    const { user: currentUser, isContentAdmin } = useUserContext();
    const { setNotification } = useNotifications();

    const { data, loading, error } = useQuery<{ beaconWrapUpStory: BeaconWrapUpStory }>(GET_BEACON_WRAP_UP_STORY, {
        skip: !id,
        variables: {
            id
        }
    });

    const { data: itemsData, error: itemsError } = useQuery<{ beaconItems: GraphQlConnection<BeaconItem> }>(GET_BEACON_ITEMS, {
        variables: {
            first: 500
        }
    });

    const [savedId, setSavedId] = React.useState<string>();
    const [upsertBeaconWrapUpStory, { error: beaconWrapUpStoryUpsertError, loading: isSavingBeaconWrapUpStory }] = useMutation<{
        beaconWrapUpStoryUpsert: BeaconWrapUpStoryInput;
    }>(UPSERT_BEACON_WRAP_UP_STORY, {
        onCompleted: (data) => {
            setSavedId(data.beaconWrapUpStoryUpsert.id);
            setNotification("Wrap-up story saved", "success");
        },
        update: (cache, mutationResponse) => {
            cache.modify({
                fields: {
                    beaconWrapUpStories(existingItems) {
                        const newStoryRef = cache.writeFragment({
                            data: mutationResponse.data?.beaconWrapUpStoryUpsert,
                            fragment: gql`
                                ${BEACON_WRAP_UP_STORY_CORE_FIELDS_FRAGMENT}
                            `,
                            fragmentName: "BeaconWrapUpStoryCoreFields"
                        });

                        return id
                            ? existingItems
                            : {
                                  ...existingItems,
                                  edges: [
                                      {
                                          node: newStoryRef
                                      },
                                      ...existingItems.edges
                                  ]
                              };
                    }
                }
            });
        }
    });

    React.useEffect(() => {
        if (error?.message || beaconWrapUpStoryUpsertError?.message || itemsError?.message) {
            setNotification(error?.message || beaconWrapUpStoryUpsertError?.message || itemsError?.message, "error");
        }
    }, [error, beaconWrapUpStoryUpsertError, itemsError, setNotification]);

    const story = id
        ? data?.beaconWrapUpStory
        : ({
              id: uuid(),
              title: "",
              author: {
                  id: currentUser?.id
              } as unknown,
              headerImageUrl: null,
              dateCreated: new Date(),
              datePublished: null,
              description: "",
              images: [],
              isPublished: false,
              isArchived: false,
              isApproved: false,
              approvedAt: null,
              approvedBy: null,
              previewDescription: "",
              selectedItems: [],
              projectId: null
          } as BeaconWrapUpStory);

    const isFetching = !story || loading;

    const beaconItems = itemsData?.beaconItems.edges.map((e) => e.node) || [];
    const clients = story?.project?.clients || [];
    const firstClientId = clients.length ? clients[0].id : null;

    const storyInput = story
        ? ({
              id: story.id,
              title: story.title,
              author: {
                  id: story.author?.id || currentUser?.id
              },
              dateCreated: story.dateCreated,
              datePublished: story.datePublished,
              isPublished: story.isPublished,
              isArchived: story.isArchived,
              projectId: story.project?.id,
              description: story.description,
              images: story.images.map((i) => ({
                  title: i.title,
                  description: i.description,
                  filename: i.filename,
                  url: i.url,
                  optimizedFilename: i.optimizedFilename,
                  optimizedUrl: i.optimizedUrl,
                  thumbnailFilename: i.thumbnailFilename,
                  thumbnailUrl: i.thumbnailUrl,
                  showInGallery: i.showInGallery,
                  showInHeader: i.showInHeader
              })),
              previewDescription: story.previewDescription,
              selectedItems: story.selectedItems.map((i) => ({
                  title: i.title,
                  explanation: i.explanation,
                  selectedItemId: i.selectedItem.id,
                  outcome: {
                      justification: i.outcome.justification,
                      sentiment: i.outcome.sentiment
                  },
                  consideredAlternativeItemIds: i.consideredAlternativeItems.map((i) => i.id)
              }))
          } as BeaconWrapUpStoryInput)
        : null;

    const canEdit =
        !!storyInput &&
        !!currentUser?.id &&
        (currentUser.id === storyInput.author?.id ||
            isContentAdmin ||
            (!!story &&
                (story.project?.participants || []).some((participant) => !!participant && participant.person.id === currentUser.id)));

    const handleSave = async (wrapUpStory: BeaconWrapUpStoryInput) => {
        await upsertBeaconWrapUpStory({
            variables: {
                beaconWrapUpStory: wrapUpStory
            }
        });
    };

    if (savedId) {
        return <Navigate to={`/wrap-up-stories/${savedId}`} />;
    }

    if (isFetching) {
        return (
            <div className="fetching-wrapper">
                <Loading text="Fetching..." />
            </div>
        );
    }

    return (
        <>
            <DocumentTitle pageTitle={`${id ? "Edit" : "New"} wrap-up story`} />
            <Page noBackground noSidePadding>
                {!!storyInput && canEdit && (
                    <WrapUpStoryEditor
                        beaconWrapUpStory={storyInput}
                        beaconItemSummaries={beaconItems}
                        onSave={handleSave}
                        isSaving={isSavingBeaconWrapUpStory}
                        clientId={firstClientId}
                        isNew={!id || id === "new"}
                    />
                )}
                {loading && <Typography>Initializing...</Typography>}
                {!loading && !!storyInput && !canEdit ? (
                    <div className="access-error">You do not have the authorization to access this page.</div>
                ) : null}
                <Snackbar
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "center"
                    }}
                    message={<span id="message-id">{error}</span>}
                    open={!!error}
                />
            </Page>
        </>
    );
};

export default WrapUpStoryEditorPage;
